import { ChangeDetectionStrategy, Component, computed, input } from '@angular/core';
import { downgradeComponent } from '@angular/upgrade/static';
import App from '@app/app';
import { LtResolvedImageComponent } from '@app/components/core/lt-resolved-image/lt-resolved-image.component';
import type { LookImage } from '@app/interface/image/look-image.interface';
import type { ResolvedImage } from '@app/interface/image/resolved-image.interface';
import { subjectImageResource } from '@app/service/image/image.resource';
import loadingPlaceholder from 'images/loading/PORTRAIT_loading_error_large.jpg';

@Component({
  selector: 'lt-look-image',
  templateUrl: './lt-look-image.component.html',
  styleUrl: './lt-look-image.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: {
    '[class]': 'hostClass()',
    // Opt into the global right-click/drag deterrent (utils/image-protection.ts).
    'data-protected-image': ''
  },
  imports: [LtResolvedImageComponent]
})
export class LtLookImageComponent {
  readonly lookImage = input.required<LookImage>();
  readonly testId = input.required<string>();
  readonly altFallback = input<string>('');
  readonly showErrorFallback = input(true);
  /** `object-fit` for the resolved image; forwarded to lt-resolved-image. Only applies when `fill` is set. */
  readonly fit = input<'fill' | 'contain' | 'cover' | 'none' | 'scale-down'>('cover');
  /** Crop focal point (`center` | `top`); forwarded to lt-resolved-image. */
  readonly focus = input<'center' | 'top'>('center');
  /** When `true`, the image fills its container so `fit` applies. */
  readonly fill = input<boolean>(false);

  private readonly imageRes = subjectImageResource(() => ({
    ...this.lookImage(),
    attrs: { ltLookNum: this.lookImage().lookNumber ?? '' }
  }));

  protected readonly orientation = computed(
    () => this.lookImage().orientation.toLowerCase() as Lowercase<LookImage['orientation']>
  );

  protected readonly resolved = computed<ResolvedImage | undefined>(() => {
    const url = this.imageRes.hasValue() ? this.imageRes.value() : undefined;
    return url ? { src: url, source: 'iris-look', altText: this.altFallback() } : undefined;
  });

  // Banner and chiclet disable the shared placeholder so the avatar can fully collapse on failure.
  protected readonly fallbackSrc = computed<string | undefined>(() => {
    if (this.imageRes.isLoading() || !this.showErrorFallback()) {
      return undefined;
    }

    return loadingPlaceholder;
  });

  protected readonly isEmpty = computed(() => !this.imageRes.isLoading() && !this.resolved() && !this.fallbackSrc());

  protected readonly hostClass = computed(() => {
    const classes: string[] = [];
    const o = this.orientation();

    if (this.isEmpty()) {
      classes.push('lt-look-image--empty');
    }

    if (this.imageRes.isLoading()) {
      classes.push(`image-loader-loading-${o}`);
    } else if (this.imageRes.error()) {
      classes.push(`image-loader-error-${o}`);
    }

    return classes.join(' ');
  });
}

App.directive('ltLookImage', downgradeComponent({ component: LtLookImageComponent }));
